import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { API_URL } from "../config";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import {
  ChevronLeft,
  ChevronRight,
  LogOut,
  MessageCircle,
  Users,
  Car,
  FileText,
  Search,
  Bell,
  BarChart2,
  BarChart3,
  Settings,
  Home,
  CreditCard,
  Radio,
  Map,
  Send,
  DollarSign,
  Sliders,
  Clock,
  Activity,
  Navigation,
  TrendingUp,
  User,
  Globe,
  Heart,
  Award,
  Route,
  Trophy,
  MapPin,
  Gift,
  Share2,
  Wallet,
  Key,
  Shield,
  AlertTriangle,
  Scale,
  Cog,
  Wrench,
  Bot,
  UserCheck,
  HelpCircle,
  Star,
  Download,
  Image,
  BookOpen,
} from "lucide-react";

const SideBar = ({ collapsed, setCollapsed }) => {
  // Accept collapsed as a prop
  const navigate = useNavigate();
  const [counts, setCounts] = useState({
    customers: 0,
    trips: 0,
    drivers: 0,
    pending: 0,
    newTrips: 0,
  });
  const [activeSection, setActiveSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);

  const APILINK = API_URL;

  const handleLogout = () => {
    console.log('User logging out - clearing session');
    
    // Clear localStorage
    localStorage.removeItem("adminId");
    localStorage.removeItem("role");
    localStorage.removeItem("userDetails");
    localStorage.removeItem("userName");
    
    // Clear inactivity timeout
    if (window.inactivityTimeout) {
      clearTimeout(window.inactivityTimeout);
      window.inactivityTimeout = null;
    }
    
    // Navigate to login page
    navigate("/");
  };

  // Set up inactivity timeout (10 minutes)
  const setupInactivityTimeout = () => {
    // Clear existing timeouts
    if (window.inactivityTimeout) {
      clearTimeout(window.inactivityTimeout);
    }
    if (window.warningTimeout) {
      clearTimeout(window.warningTimeout);
    }
    
    // Show warning 1 minute before logout (9 minutes = 540000 ms)
    window.warningTimeout = setTimeout(() => {
      const userName = localStorage.getItem("userName");
      if (userName) {
        console.log('Session expiring in 1 minute - showing warning');
        // Show warning notification
        if (window.Swal) {
          Swal.fire({
            title: 'Session Expiring Soon!',
            text: 'Your session will expire in 1 minute due to inactivity. Click anywhere to continue.',
            icon: 'warning',
            timer: 60000, // 1 minute
            timerProgressBar: true,
            showConfirmButton: true,
            confirmButtonText: 'Keep Me Logged In',
            showCancelButton: true,
            cancelButtonText: 'Logout Now',
            allowOutsideClick: true,
            allowEscapeKey: true
          }).then((result) => {
            if (result.isConfirmed) {
              // User wants to stay logged in - reset timeout
              resetInactivityTimeout();
              Swal.fire('Extended!', 'Your session has been extended.', 'success');
            } else if (result.isDismissed || result.isCancelled) {
              // User dismissed or clicked logout
              handleLogout();
            }
          });
        } else {
          // Fallback if Swal is not available
          alert('Your session will expire in 1 minute due to inactivity. Click anywhere to continue.');
        }
      }
    }, 540000); // 9 minutes
    
    // Set logout timeout for 10 minutes (600000 ms)
    window.inactivityTimeout = setTimeout(() => {
      console.log('User inactive for 10 minutes - auto logout');
      if (window.Swal) {
        Swal.fire({
          title: 'Session Expired',
          text: 'You have been logged out due to inactivity.',
          icon: 'info',
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
          allowOutsideClick: false
        });
      }
      handleLogout();
    }, 600000);
    
    console.log('Inactivity timeout set for 10 minutes with warning at 9 minutes');
  };

  // Reset inactivity timeout on user activity
  const resetInactivityTimeout = () => {
    const userName = localStorage.getItem("userName");
    if (userName) {
      setupInactivityTimeout();
    }
  };

  const fetchJsonSafe = async (url) => {
    try {
      const res = await fetch(url);
      if (!res.ok) return [];
      return await res.json();
    } catch (e) {
      return [];
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [trips, newTrips, pending, drivers, customers] = await Promise.all([
        fetchJsonSafe(`${APILINK}/trip/`),
        fetchJsonSafe(
          `${APILINK}/trip/tripsbystatus/${encodeURIComponent("New Order")}`
        ),
        fetchJsonSafe(
          `${APILINK}/driver/driver_status/${encodeURIComponent(
            "Pending Verification"
          )}`
        ),
        fetchJsonSafe(`${APILINK}/driver/`),
        axios.get(`${APILINK}/customerdetails/`).then((res) => res.data),
      ]);
      console.log("ggggg", pending);
      setCounts({
        trips: trips.length,
        newTrips: newTrips.length,
        pending: pending.length,
        drivers: drivers.length,
        customers: customers.length,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Global authentication check
    const checkAuthentication = () => {
      const userName = localStorage.getItem("userName");
      const adminId = localStorage.getItem("adminId");
      const userDetails = localStorage.getItem("userDetails");
      
      console.log('Sidebar Auth Check:', {
        userName: !!userName,
        adminId: !!adminId,
        userDetails: !!userDetails
      });
      
      // If no username is set, user is not logged in - redirect to login
      if (!userName || userName.trim() === '') {
        console.log('No username found - redirecting to login');
        // Clear inactivity timeout
        if (window.inactivityTimeout) {
          clearTimeout(window.inactivityTimeout);
          window.inactivityTimeout = null;
        }
        window.location.href = '/';
        return;
      }
      
      // User is authenticated, proceed with normal functionality
      console.log('User authenticated:', userName);
      fetchData();
      
      // Set up inactivity timeout for authenticated users
      setupInactivityTimeout();
    };
    
    checkAuthentication();
    
    // Set up periodic checks (every 30 seconds)
    const authInterval = setInterval(checkAuthentication, 30000);
    
    // Set up data fetching interval
    const dataInterval = setInterval(() => {
      const userName = localStorage.getItem("userName");
      if (userName) {
        fetchData();
      }
    }, 7000);
    
    // Add event listeners for user activity to reset inactivity timeout
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleUserActivity = () => {
      resetInactivityTimeout();
    };
    
    activityEvents.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });
    
    return () => {
      clearInterval(authInterval);
      clearInterval(dataInterval);
      
      // Remove event listeners
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
      
      // Clear inactivity timeout on cleanup
      if (window.inactivityTimeout) {
        clearTimeout(window.inactivityTimeout);
        window.inactivityTimeout = null;
      }
      if (window.warningTimeout) {
        clearTimeout(window.warningTimeout);
        window.warningTimeout = null;
      }
    };
  }, []);

  const menuSections = [
    {
      title: "Dashboard",
      items: [
        {
          name: "Admin Dashboard",
          path: "/Admindash",
          icon: <Activity size={18} />,
          count: null,
        },
        {
          name: "Heat Map Ride Demand",
          path: "/heatmap-ride-demand",
          icon: <Activity size={18} />,
          count: null,
        },
        {
          name: "Active Drivers Locations Map",
          path: "/active-drivers-locations-map",
          icon: <Radio size={18} />,
          count: null,
        },
        {
          name: "Customer Map",
          path: "/customers-map",
          icon: <Map size={18} />,
          count: null,
        },
      ],
    },
    {
      title: "User Management",
      items: [
        {
          name: "Customers",
          path: "/Customerstable/list",
          icon: <Users size={18} />,
          count: counts.customers,
        },
        {
          name: "Customers Filter Table",
          path: "/Customers",
          icon: <Users size={18} />,
          count: counts.customers,
        },
        {
          name: "Drivers",
          path: "/Driverstable/list",
          icon: <Car size={18} />,
          count: counts.drivers,
        },
        {
          name: "Drivers Filter Table",
          path: "/Drivers",
          icon: <Car size={18} />,
          count: null,
        },
        {
          name: "Pending Driver Verification",
          path: "/verifyReg",
          icon: <Bell size={18} />,
          count: counts.pending,
        },
        {
          name: "Search User",
          path: "/search",
          icon: <Search size={18} />,
          count: null,
        },
      ],
    },
    {
      title: "Trip Management",
      items: [
        {
          name: "All Trips",
          path: "/trips",
          icon: <Navigation size={18} />,
          count: counts.trips,
        },
        {
          name: "Trips Filter Table",
          path: "/TripsFilter",
          icon: <Navigation size={18} />,
          count: counts.trips,
        },
        {
          name: "Allocate Trips",
          path: "/TripAllocate",
          icon: <Send size={18} />,
          count: counts.newTrips,
        },
        {
          name: "Search Trip",
          path: "/searchTrip",
          icon: <Search size={18} />,
          count: null,
        },
        {
          name: "Trip Tracking",
          path: "/DashTracker/1",
          icon: <Map size={18} />,
          count: null,
        },
      ],
    },
    {
      title: "Finance & Payments",
      items: [
               {
          name: "Financial Overview",
          path: "/financial-overview",
          icon: <Activity size={18} />,
          count: null,
        },
               {
          name: "Financial System Guide",
          path: "/financial-system-guide",
          icon: <BookOpen size={18} />,
          count: null,
        },
               {
          name: "Transactions",
          path: "/GroupedTransactionTable",
          icon: <DollarSign size={18} />,
          count: null,
        },
         {
          name: "Account Top Up",
          path: "/billing",
          icon: <CreditCard size={18} />,
          count: null,
        },
       {
          name: "Enhanced Promotions",
          path: "/enhanced-promotions",
          icon: <Gift size={18} />,
          count: null,
        },
        {
          name: "Routine Reminders",
          path: "/routine-reminders",
          icon: <Gift size={18} />,
          count: null,
        },
        {
          name: "Withdrawal Management",
          path: "/withdrawal-management",
          icon: <Wallet size={18} />,
          count: null,
        },
        {
          name: "Payment Reconciliation",
          path: "/payment-reconciliation",
          icon: <FileText size={18} />,
          count: null,
        },
      ],
    },
        // Teller Operations Section
    {
      title: "Teller Operations",
      icon: <Scale />,
      items: [
        {
          name: "Teller Control Panel",
          path: "/teller-control-panel",
          icon: <Scale size={18} />,
          count: null,
        },
      ],
    },
    {
      title: "Analytics & Reports",
      items: [
        {
          name: "Analytics Dashboard",
          path: "/analytics",
          icon: <BarChart2 size={18} />,
          count: null,
        },
        {
          name: "Channel Attribution",
          path: "/channel-attribution",
          icon: <Radio size={18} />,
          count: null,
        },
        {
          name: "Referral Tracking",
          path: "/referral-tracking",
          icon: <Users size={18} />,
          count: null,
        },
        {
          name: "Customer Analytics",
          path: "/customer-analytics-dashboard",
          icon: <Users size={18} />,
          count: null,
        },
        {
          name: "Driver Analytics",
          path: "/driver-analytics-dashboard",
          icon: <Car size={18} />,
          count: null,
        },
        {
          name: "Driver Earnings",
          path: "/driver-earnings",
          icon: <Wallet size={18} />,
          count: null,
        },
        {
          name: "Customer Performance",
          path: "/customer-performance",
          icon: <User size={18} />,
          count: null,
        },
        {
          name: "Rating Analytics",
          path: "/stats",
          icon: <Star size={18} />,
          count: null,
        },
        {
          name: "Comprehensive Analytics",
          path: "/comprehensive-analytics",
          icon: <BarChart3 size={18} />,
          count: null,
        },
        {
          name: "Notification Analytics",
          path: "/notification-analytics",
          icon: <Bell size={18} />,
          count: null,
        },
        {
          name: "Funnel Analysis",
          path: "/funnel",
          icon: <TrendingUp size={18} />,
          count: null,
        },

      ],
    },
    {
      title: "Statistics & Monitoring",
      items: [
        {
          name: "App Statistics",
          path: "/AppStatistics",
          icon: <Activity size={18} />,
          count: null,
        },
        {
          name: "Statistics Charts",
          path: "/statsCharts",
          icon: <Activity size={18} />,
          count: null,
        },
        {
          name: "General Statistics",
          path: "/stats",
          icon: <Activity size={18} />,
          count: null,
        },
        {
          name: "Hourly Statistics",
          path: "/hourly",
          icon: <Clock size={18} />,
          count: null,
        },
      ],
    },
    {
      title: "System Configuration",
      items: [
        {
          name: "Configurations",
          path: "/Configurations",
          icon: <Sliders size={18} />,
          count: null,
        },
        {
          name: "Realtime Settings",
          path: "/commission-control",
          icon: <Settings size={18} />,
          count: null,
        },
        {
          name: "Shared Rides",
          path: "/shared-rides-settings",
          icon: <Sliders size={18} />,
          count: null,
        },
        {
          name: "Morning Dash",
          path: "/morning-dash",
          icon: <Sliders size={18} />,
          count: null,
        },
        {
          name: "Ride Intents",
          path: "/ride-intents",
          icon: <Sliders size={18} />,
          count: null,
        },
        {
          name: "Detour Distance Control",
          path: "/detour-distance-control",
          icon: <MapPin size={18} />,
          count: null,
        },
        {
          name: "Notification Settings",
          path: "/notification-settings",
          icon: <Bell size={18} />,
          count: null,
        },
        {
          name: "Bot Greetings",
          path: "/bot-greetings",
          icon: <MessageCircle size={18} />,
          count: null,
        },
        {
          name: "Referral Messages",
          path: "/referral-messages",
          icon: <Share2 size={18} />,
          count: null,
        },
        {
          name: "Enhanced Driver Analytics",
          path: "/enhanced-driver-analytics",
          icon: <Trophy size={18} />,
          count: null,
        },
        {
          name: "Service Vehicle Type Management",
          path: "/vehicle-management",
          icon: <Car size={18} />,
          count: null,
        },
      ],
    },
    {
      title: "Communication",
      items: [
        {
          name: "Recent Feedback",
          path: "/feedback",
          icon: <Star size={18} />,
          count: null,
        },
        {
          name: "Broadcast to Drivers",
          path: "/broadcast",
          icon: <Radio size={18} />,
          count: null,
        },
        {
          name: "Broadcast to Customers",
          path: "/BroadcastCust",
          icon: <Radio size={18} />,
          count: null,
        },
      ],
    },
    {
      title: "Support & Tickets",
      items: [
        {
          name: "Create Ticket",
          path: "/TicketCreationPage",
          icon: <Search size={18} />,
          count: null,
        },
        {
          name: "Assign Chats",
          path: "/AllocateChats",
          icon: <Search size={18} />,
          count: null,
        },
        {
          name: "Assign Tickets",
          path: "/AllocateTickets",
          icon: <Sliders size={18} />,
          count: null,
        },
        {
          name: "Customer Service Chats",
          path: "/CustomerServiceHome",
          icon: <Sliders size={18} />,
          count: null,
        },
        {
          name: "Assigned Tickets",
          path: "/tickets",
          icon: <Sliders size={18} />,
          count: null,
        },
        {
          name: "Agent Dashboard",
          path: "/TechSupportHome",
          icon: <UserCheck size={18} />,
          count: null,
        },
        {
          name: "Auto-Assignment Settings",
          path: "/assignment-settings",
          icon: <Bot size={18} />,
          count: null,
        },
        {
          name: "FAQ Management",
          path: "/faq-management",
          icon: <HelpCircle size={18} />,
          count: null,
        },
      ],
    },
    // Vehicle Ownership & Drive to Own Section — was "Vehicle Rental Management" (a stale name
    // left over from when this section had more, since-removed items), renamed 2026-09-16 so
    // Drive to Own is actually findable in the sidebar instead of hiding under an unrelated title.
    {
      title: "Vehicle Ownership & Drive to Own",
      icon: <Car />,
      items: [
        {
          name: "Vehicle Ownership & Rates",
          path: "/vehicle-ownership-settings",
          icon: <Car size={18} />,
          count: null,
        },
        {
          name: "Drive to Own",
          path: "/drive-to-own-applications",
          icon: <Car size={18} />,
          count: null,
        },
      ],
      // "Vehicle Rental Dashboard" (100% mock data over a confirmed-broken legacy backend) and
      // "Vehicle Maintenance Schedule" (linked to a route that was never built at all — a real
      // white-screen bug, not a rendering issue) were both removed 2026-09-15. See
      // KNOWN_ISSUES_AND_FIX_BACKLOG.md.
    },

    // Driver Configuration Section
    {
      title: "Driver Configuration",
      icon: <Cog />,
      items: [
        {
          name: "Driver Configuration Management",
          path: "/driver-configuration",
          icon: <Cog size={18} />,
          count: null,
        },
      ],
    },
    {
      title: "Performance Reports",
      icon: <BarChart3 />,
      items: [
        {
          name: "Performance Dashboard",
          path: "/performance-reports",
          icon: <TrendingUp />,
          count: null,
        },
        {
          name: "Trips Status Report",
          path: "/trips-status-report",
          icon: <Activity />,
          count: null,
        },
        {
          name: "Geographic Performance",
          path: "/geographic-report",
          icon: <Globe />,
          count: null,
        },
        {
          name: "Peak Hours Analysis",
          path: "/peak-hours-report",
          icon: <Clock />,
          count: null,
        },
        {
          name: "Revenue Breakdown",
          path: "/revenue-breakdown-report",
          icon: <DollarSign />,
          count: null,
        },
        {
          name: "Driver Performance",
          path: "/driver-performance-report",
          icon: <Users />,
          count: null,
        },
        {
          name: "Customer Analytics & Retention",
          path: "/customer-analytics-report",
          icon: <Heart />,
          count: null,
        },
        {
          name: "Customer Performance",
          path: "/customer-performance-report",
          icon: <Award />,
          count: null,
        },
        {
          name: "Rideshare Analytics",
          path: "/rideshare-analytics-report",
          icon: <Car />,
          count: null,
        },
        {
          name: "Private Ride Analytics",
          path: "/private-ride-analytics-report",
          icon: <Map />,
          count: null,
        },
        {
          name: "Wait Time Monitor",
          path: "/wait-time-monitor",
          icon: <Clock />,
          count: null,
        },
        {
          name: "Top 20 Drivers by Trips",
          path: "/toptwentydriverscount",
          icon: <Users />,
          count: null,
        },
        {
          name: "Top 20 Customers by Trips",
          path: "/toptwentycustomerscount",
          icon: <Users />,
          count: null,
        },
        {
          name: "Lowest 20 Drivers by Trips",
          path: "/lowtwentydriverscount",
          icon: <Search size={18} />,
          count: null,
        },
        {
          name: "Lowest 20 Customers by Trips",
          path: "/lowtwentycustomerscount",
          icon: <Search size={18} />,
          count: null,
        },
      ],
    },
    {
      title: "Operations",
      icon: <Wrench />,
      items: [
        {
          name: "Image Gallery",
          path: "/image-gallery",
          icon: <Image />,
          count: null,
        },
        {
          name: "Road Assistance",
          path: "/road-assistance",
          icon: <Wrench />,
          count: null,
        },
      ],
    },


         {
      title: "Image Gallery",
      icon: <Wrench />,
      items: [       
        {
          name: "Image Gallery",
          path: "/image-gallery",
          icon: <Download size={18} />,
          count: null,
        }]
      },
   {
      title: "App Downloads",
      icon: <Wrench />,
      items: [       
        {
          name: "App Download Analytics",
          path: "/app-download-analytics",
          icon: <Download size={18} />,
          count: null,
        }]
      }


        ];
  const toggleSection = (index) => {
    setActiveSection(activeSection === index ? null : index);
  };

  return (
    <motion.div
      className={`relative bg-[#002966] text-white h-full shadow-2xl border-r border-blue-900/30`}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-blue-800/50">
        {!collapsed && (
          <motion.div
            className="flex items-center space-x-3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center shadow-lg">
            </div>
            <h2 className="text-xl font-bold text-yellow-400">
              ADMIN PANEL
            </h2>
          </motion.div>
        )}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setCollapsed(!collapsed)}
          className="p-2 rounded-lg bg-blue-800/50 hover:bg-blue-700/50 transition-colors duration-200 shadow-md"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </motion.button>
      </div>

      {/* Menu Items */}
      <div className="mt-4">
        <div
          className={`overflow-y-auto ${
            collapsed ? "h-[calc(100vh-180px)]" : "h-[calc(100vh-220px)]"
          } custom-scrollbar`}
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#1e3a8a transparent' }}
        >
          {menuSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-3">
              {/* Section Header */}
              {!collapsed && (
                <motion.div
                  className="px-4 py-2 text-xs font-semibold text-blue-200 uppercase tracking-wider cursor-pointer flex items-center justify-between hover:text-blue-100 transition-colors"
                  onClick={() => toggleSection(sectionIndex)}
                  whileHover={{ x: 2 }}
                >
                  <span className="text-blue-200 normal-case">{section.title}</span>
                  <ChevronRight
                    size={12}
                    className={`transform transition-transform duration-200 text-blue-400 ${
                      activeSection === sectionIndex ? "rotate-90" : ""
                    }`}
                  />
                </motion.div>
              )}

              {/* Section Items */}
              <motion.div
                animate={{
                  height: collapsed
                    ? "auto"
                    : activeSection === sectionIndex
                    ? "auto"
                    : "0px",
                  opacity: collapsed
                    ? 1
                    : activeSection === sectionIndex
                    ? 1
                    : 0,
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className={`overflow-hidden ${
                  !collapsed && activeSection !== sectionIndex ? "h-0" : ""
                }`}
              >
                {section.items.map((item, itemIndex) => (
                  <motion.a
                    key={itemIndex}
                    href={item.path}
                    whileHover={{ x: 4 }}
                    whileTap={{ scale: 0.98 }}
                    className={`flex items-center justify-between ${
                      collapsed ? "px-3 py-3" : "px-4 py-3"
                    } mx-2 mb-1 rounded-lg bg-blue-800/30 hover:bg-blue-700/50 transition-all duration-200 group shadow-sm`}
                  >
                    <div className="flex items-center flex-1">
                      <div className="text-yellow-400 group-hover:text-yellow-300 transition-colors">
                        {item.icon}
                      </div>
                      {!collapsed && (
                        <span className="ml-3 text-sm font-medium text-gray-100 group-hover:text-white transition-colors">
                          {item.name}
                        </span>
                      )}
                    </div>
                    {!collapsed && item.count !== null && (
                      <motion.div
                        className={`${
                          item.count > 0
                            ? "bg-yellow-500 text-[#002966]"
                            : "bg-gray-600 text-gray-300"
                        } text-xs font-bold px-2 py-1 rounded-full shadow-md`}
                        initial={{ scale: 0.8 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 500 }}
                      >
                        {item.count}
                      </motion.div>
                    )}
                    {collapsed && item.count !== null && item.count > 0 && (
                      <motion.div
                        className="absolute top-2 right-2 w-2 h-2 bg-yellow-500 rounded-full"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                      />
                    )}
                  </motion.a>
                ))}
              </motion.div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-blue-800/50 p-3">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleLogout}
          className={`w-full p-2 rounded-lg bg-red-900/50 hover:bg-red-800/50 text-red-200 text-sm font-medium transition-colors duration-200 flex items-center justify-center ${
            collapsed ? "px-2" : "px-4"
          } shadow-md`}
        >
          <LogOut size={16} className={collapsed ? "" : "mr-2"} />
          {!collapsed && "Logout"}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default SideBar;
